<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('purchaseRequest.pageTitle') }}</h1>
      <ts-breadcrumb :items="[
        { text: $t('home'), href: '/' },
        {
          text: $t('purchaseRequest.pageTitle'),
          active: true
        }
      ]" />
    </div>
    <ts-card>
      <ts-card-wrapper>
        <div class="d-flex justify-content-between ts-items-center ts-whitespace-no-wrap ts-space-x-3">
          <div class="ts-flex ts-items-center ts-space-x-3">
            <ts-button outline color="info" @click.prevent="addNew">
              {{ $t('add_new') }}
            </ts-button>
            <a-range-picker v-model="dateRange" :allowClear="false" format="DD-MM-YYYY" valueFormat="DD-MM-YYYY"
              class="ts-w-64" @change="fetchData()" />
          </div>
          <div class="d-flex ts-space-x-3 ts-items-center">
            <Tooltip :content="$t('purchaseRequest.search')" placement="top" transfer :max-width="width">
              <Input search v-model="search" :placeholder="$t('purchaseRequest.search')" :style="width"
                @on-focus="() => (width = 'width: 500px')" @on-blur="() => (width = 'width: 300px')" />
            </Tooltip>
            <ts-filter ref="filter">
              <div class="row">
                <div class="col-md-12">
                  <label class="">{{ $t('employeeProfile.branch') }}</label>
                  <ts-branch-filter-v2 @filter="branchFilter" class="ts-w-full"></ts-branch-filter-v2>
                </div>
              </div>
            </ts-filter>
          </div>
        </div>
      </ts-card-wrapper>
      <Table :columns="columns" :data="resources" size="small" :loading="loading" highlight-row stripe
        max-height="700">
        <template slot-scope="{ row }" slot="branch">
          {{ row.branch ? row.branch.branch_name_en : 'N/A' }}
        </template>
        <template slot-scope="{ row }" slot="pr_by_emp">
          {{ row.pr_by_emp ? row.pr_by_emp.employee_name_en : 'N/A' }}
        </template>
        <template slot-scope="{ row }" slot="project">
          {{ row.project ? row.project.project_name : 'N/A' }}
        </template>
        <template slot-scope="{ row }" slot="customer">
          {{ row.customer ? row.customer.customer_name : 'N/A' }}
        </template>
        <template slot-scope="{ row }" slot="approve_status">
          <span class="badge bg-secondary" v-if="row.approve_status == 0">
            {{ row.approve_status_name ? row.approve_status_name : 'N/A' }}
          </span>
          <span class="badge bg-info" v-if="row.approve_status == 1">
            {{ row.approve_status_name ? row.approve_status_name : 'N/A' }}
          </span>
          <span class="badge bg-success" v-if="row.approve_status == 2">
            {{ row.approve_status_name ? row.approve_status_name : 'N/A' }}
          </span>
          <span class="badge bg-danger" v-if="row.approve_status == 3">
            {{ row.approve_status_name ? row.approve_status_name : 'N/A' }}
          </span>
        </template>
        <template slot-scope="{ row }" slot="approved_by">
          {{
            row.approved_by_user ? row.approved_by_user.employee_name_en : 'N/A'
          }}
        </template>
        <template slot-scope="{ row }" slot="create_po">
          <span class="badge badge-success" v-if="row.purchase_orders.length > 0">Yes</span>
          <span class="badge badge-danger" v-else>No</span>
        </template>
        <template slot-scope="{ row }" slot="create_by">
          {{ row.create_by ? row.create_by.employee_name_en : 'N/A' }}
        </template>
        <template slot-scope="{ row }" slot="remarks">
          <Tooltip :content="row.remarks" max-width="500" theme="light" :delay="500">
            <div class="ts-whitespace-no-wrap">
              {{ row.remarks ? row.remarks : '' }}
            </div>
          </Tooltip>
        </template>
        <template slot-scope="{ row }" slot="action">
          <div class="ts-space-x-2 ts-whitespace-no-wrap">
            <a v-if="row.is_reversed" href="#" v-tooltip="$t('purchaseRequest.reverse')" class="ts-text-black"
              @click.prevent="showMessageReversed(row)">
              <i class="fas fa-history"></i>
            </a>
            <a v-if="checkApproval(row)" href="#" v-tooltip="$t('purchaseRequest.confirmation')" class="text-green"
              @click.prevent="confirm(row)">
              <i class="fas fa-check-circle"></i>
            </a>
            <a href="#" v-tooltip="$t('pdf')" class="text-red" @click.prevent="reprintReport(row)">
              <i class="fas fa-file-pdf"></i>
            </a>
            <a v-if="row.approve_status == 2 && !isComplete(row)" href="#" v-tooltip="$t('purchaseRequest.createPo')"
              class="text-green" @click.prevent="createPo(row)">
              <i class="fas fa-plus-circle"></i>
            </a>
            <a v-if="row.approve_status == 0" href="#" v-tooltip="$t('edit')" class="text-primary"
              @click.prevent="edit(row)">
              <i class="fas fa-edit"></i>
            </a>
            <a-popconfirm v-if="row.approve_status == 0" v-tooltip="$t('delete')" placement="leftTop"
              :title="$t('are_you_sure_to_delete')" :ok-text="$t('yes')" :cancel-text="$t('no')"
              @confirm="deleteRecord(row)">
              <a href="#" :disabled="row._deleting">
                <i class="fas fa-circle-notch spin" v-if="row._deleting"></i>
                <i class="fa fa-trash text-red" v-else></i>
              </a>
            </a-popconfirm>
          </div>
        </template>
      </Table>
      <div class="d-flex justify-content-between align-items-center p-15">
        <ts-total-list v-model="pagination" />
        <ts-i-pagination v-model="pagination" @navigate="fetchData"></ts-i-pagination>
      </div>
      <!-- modal show pop-up pdf -->
      <a-modal v-model="show_pdf" :title="$t('pdf')" width="50%" :footer="null">
        <ts-preview-pdf v-model="waiting" :src="src" />
      </a-modal>
    </ts-card>

    <a-modal v-model="showMessage" :footer="null" centered :title="$t('purchaseRequest.reverse')" @cancel="onCloseReversed">
      <a-timeline mode="alternate" v-if="reversedMessage">
        <a-timeline-item>Created at {{ reversedMessage ? reversedMessage.created_date : '' }}</a-timeline-item>
        <a-timeline-item v-for="(app, index) in reversedMessage.approval_user"
          :color="app.is_reversed && app.app_status == 0 ? 'red' : 'green'">
          <a-icon slot="dot" type="user" style="font-size: 16px;" />
          <p class="ts-mb-0"><b>{{ app.employee.employee_name_kh }}</b></p>
          <p class="ts-mb-0 ts-text-gray-400">{{ app.employee.employee_name_en }}</p>
          <p class="ts-mb-0 ts-text-gray-400">Level: {{ app.app_level }}</p>
          <p class="ts-mb-0 ts-text-gray-400">Status: {{ statusText(app.app_status, app.is_reversed) }}</p>
          <div class="ts-border ts-rounded-md ts-border-dashed ts-p-2">
            {{ app.app_comment || app.reversed_comment }}
          </div>
        </a-timeline-item>
      </a-timeline>
    </a-modal>

  </div>
</template>

<script>
import { debounce, sumBy } from 'lodash'
import { mapActions, mapState } from 'vuex'
import expandRow from './table-expand.vue'
import { Errors } from 'form-backend-validation'

export default {
  name: 'profileIndex',
  data() {
    return {
      loading: false,
      waiting: false,
      show_pdf: false,
      show_confirm: false,
      waiting_confirm: false,
      width: 'width: 300px',
      src: '',
      errors: new Errors(),
      showMessage: false,
      reversedMessage: undefined,
      model_confirm: {
        pr_main_id: null,
        approve_status: null,
        comments: ''
      }
    }
  },
  computed: {
    ...mapState('purchaseRequest/purchaseRequest', ['resources', 'pagination']),
    statusText: () => (status, isReversed) => {
      switch (status) {
        case 0:
          return isReversed && status == 0 ? 'Reversed' : 'Pending';
        case 1:
          return 'Partly Approved';
        case 2:
          return 'Approved';
        case 3:
          return 'Reject';
        default:
          return '';
          break;
      }
    },
    isComplete: () => row => {
      const poReceived = row.details[0]
        ? row.details[0].purchase_order_received
        : 0
      if (!poReceived) return false

      const prTotal = sumBy(row.details, 'quantity')

      return prTotal - poReceived === 0
    },
    search: {
      get() {
        return this.$store.state.purchaseRequest.purchaseRequest.search
      },
      set(newValue) {
        this.$store.commit(
          'purchaseRequest/purchaseRequest/SET_SEARCH',
          newValue
        )
        this.$store.commit('purchaseRequest/purchaseRequest/RESET_CURRENT_PAGE')
      }
    },
    dateRange: {
      get() {
        return this.$store.state.purchaseRequest.purchaseRequest.dateRange
      },
      set(newValue) {
        this.$store.commit(
          'purchaseRequest/purchaseRequest/SET_DATE_RANGE',
          newValue
        )
      }
    },
    columns() {
      return [
        {
          type: 'expand',
          render: (h, params) => {
            return h(expandRow, {
              props: {
                row: params.row
              }
            })
          },
          width: 30
        },
        {
          title: this.$t('purchaseRequest.prDate'),
          key: 'pr_date',
          minWidth: 120,
          sortable: true
        },
        {
          title: this.$t('purchaseRequest.branchName'),
          slot: 'branch',
          minWidth: 140,
          sortable: true
        },
        {
          title: this.$t('purchaseRequest.prNumber'),
          key: 'pr_no',
          minWidth: 130,
          sortable: true
        },
        {
          title: this.$t('purchaseRequest.prBy'),
          slot: 'pr_by_emp',
          minWidth: 130,
          sortable: true
        },
        {
          title: this.$t('purchaseRequest.requiredDate'),
          key: 'required_date',
          minWidth: 140,
          sortable: true
        },
        {
          title: this.$t('purchaseRequest.project'),
          slot: 'project',
          minWidth: 120,
          sortable: true
        },
        {
          title: this.$t('purchaseRequest.customer'),
          slot: 'customer',
          minWidth: 150,
          sortable: true
        },
        {
          title: this.$t('purchaseRequest.approveStatus'),
          slot: 'approve_status',
          minWidth: 150,
          align: 'center',
          sortable: true
        },
        {
          title: this.$t('purchaseRequest.currentAppLevel'),
          key: 'current_app_level',
          minWidth: 180,
          align: 'center',
          sortable: true
        },
        {
          title: this.$t('purchaseRequest.approvedBy'),
          slot: 'approved_by',
          minWidth: 140,
          align: 'center',
          sortable: true
        },
        {
          title: this.$t('purchaseRequest.createPo'),
          slot: 'create_po',
          minWidth: 140,
          align: 'center',
          sortable: true
        },
        {
          title: this.$t('purchaseRequest.createdBy'),
          slot: 'create_by',
          minWidth: 150,
          sortable: true
        },
        {
          title: this.$t('purchaseRequest.remarks'),
          slot: 'remarks',
          width: 200,
          sortable: true
        },
        {
          title: this.$t('actions'),
          key: 'action',
          slot: 'action',
          fixed: 'right',
          align: 'center',
          width: 200
        }
      ]
    }
  },
  methods: {
    ...mapActions('purchaseRequest/purchaseRequest', ['getFormViewModel']),
    branchFilter(value) {
      this.$store.commit('purchaseRequest/purchaseRequest/SET_BRANCH_ID', value)
      this.fetchData()
    },
    checkApproval(row) {
      const is_in_approval = row.approval_user.find(
        el =>
          el.app_level == row.current_app_level &&
          el.app_user_id == this.$store.state.authUser.user.user_id
      )
      if ([0, 1].includes(row.approve_status) && is_in_approval) {
        return true
      } else {
        return false
      }
    },
    fetchData(attributes) {
      this.loading = true
      this.$store
        .dispatch('purchaseRequest/purchaseRequest/fetch', { ...attributes })
        .then(response => {
          this.loading = false
        })
        .catch(error => {
          this.loading = false
          this.$toasted.error(error.message)
        })
    },
    createPo(row) {
      this.$router.push({
        name: 'purchase-order-create-from-pr',
        params: { id: row.pr_main_id }
      })
    },
    confirm(row) {
      this.$router.push({
        name: 'purchase-request-confirm',
        params: { id: row.pr_main_id }
      })
    },
    addNew() {
      this.$router.push({ name: 'purchase-request-create' })
    },
    edit(record) {
      this.$router.push({
        name: 'purchase-request-edit',
        params: { id: record.pr_main_id }
      })
    },
    deleteRecord(record) {
      record._deleting = true
      this.$store
        .dispatch('purchaseRequest/purchaseRequest/destroy', record.pr_main_id)
        .then(response => {
          this.$toasted.success(response.message)
          this.fetchData()
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          record._deleting = false
        })
    },
    reprintReport(record) {
      this.show_pdf = true
      this.waiting = true
      this.src = ''
      this.$store
        .dispatch('purchaseRequest/purchaseRequest/printPdf', record.pr_main_id)
        .then(response => {
          this.src = response.url
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.waiting = false
        })
    },
    showMessageReversed(row) {
      this.reversedMessage = row
      this.showMessage = true
    },
    onCloseReversed() {
      this.reversedMessage = undefined
      this.showMessage = false
    }
  },
  watch: {
    search: debounce(function () {
      this.fetchData()
    }, 500)
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.fetchData()
    })
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit('purchaseRequest/purchaseRequest/RESET_STATE')
    next()
  }
}
</script>
<style>
.ivu-input-with-suffix {
  height: 34px !important;
}
</style>
